import type StoreService from '@ember-data/store';
import { OpenSearchQueryBuilder } from './open-search-builder';
import { OpenSearchResponseBuilder } from './open-search-response-builder';

export interface EmployeeLookupResult {
  employeeId: string;
  fullName: string;
  firstName: string;
  lastName: string;
  email?: string;
}

// Internal type for OpenSearch Person response after normalization
// OpenSearch returns employee_ids as array of numbers, which gets normalized to employeeIds
interface PersonResponse {
  id: string;
  firstName: string;
  lastName: string;
  fullName?: string;
  email: string;
  employeeIds?: number[]; // Normalized from employee_ids - these are the actual employee IDs (not payroll IDs)
}

/**
 * Fetch employee/person records by employee IDs using OpenSearch
 *
 * @param store - Ember Data store service
 * @param employeeIds - Array of employee IDs (numbers or strings) to lookup
 * @returns Map of employee ID (as string) to person data
 *
 * @example
 * ```typescript
 * const employees = await lookupEmployeesByIds(this.store, ['123', '456', '789']);
 * const name = employees.get('123')?.fullName ?? 'Unknown';
 * ```
 */
export async function lookupEmployeesByIds(
  store: StoreService,
  employeeIds: Array<string | number>
): Promise<Map<string, EmployeeLookupResult>> {
  if (!employeeIds.length) {
    return new Map();
  }

  // Convert all IDs to strings for consistent handling
  const stringIds = employeeIds.map((id) => String(id));

  // Build OpenSearch query - CRITICAL: Use snake_case for OpenSearch fields
  // We search by employee_ids which matches against the all_payroll_ids field
  const builder = new OpenSearchQueryBuilder()
    .setPagination(1, stringIds.length) // Get all results in one page
    .mustTerms('employee_ids', stringIds); // Filter by employee IDs

  const query = {
    'model-type': 'Person',
    'search-body': builder.build(),
  };

  // Execute search via search adapter
  const response = await store.adapterFor('search').search(query);

  // Parse response using OpenSearchResponseBuilder
  const responseBuilder = OpenSearchResponseBuilder.from<PersonResponse>(response);
  const documents = responseBuilder.getDocuments();

  // Build result map: employeeId -> person data
  const resultMap = new Map<string, EmployeeLookupResult>();

  documents.forEach((person) => {
    // Person documents can have multiple employee IDs (multi-company employees)
    // The employee_ids field from OpenSearch gets normalized to employeeIds (camelCase)
    // Convert numbers to strings for comparison with our stringIds
    const personEmployeeIds = (person.employeeIds || []).map((id) => String(id));

    // Match the requested IDs to this person record (comparing as strings)
    const matchingIds = stringIds.filter((id) => personEmployeeIds.includes(id));

    matchingIds.forEach((empId) => {
      resultMap.set(empId, {
        employeeId: empId,
        fullName: person.fullName || `${person.firstName || ''} ${person.lastName || ''}`.trim(),
        firstName: person.firstName || '',
        lastName: person.lastName || '',
        email: person.email,
      });
    });
  });

  return resultMap;
}
